import { api } from '@/lib/api';
import type { LocalizedText, ProjectFormData } from '@fame/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type { Activity } from './use-activities';
import type { Insurance } from './use-insurances';

export interface Project {
  id: string;
  name: LocalizedText;
  depositArticleNumber: string;
  singleRoomArticleNumber: string;
  balanceArticleNumber: string;
  activityArticleNumber: string;
  activityDescription: LocalizedText;
  defaultCostUnit: string | null;
  depositAmount: string | null;
  mollieProfile: 'nocc' | 'cc';
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  groups?: Group[];
  activities?: Activity[];
  insurances?: Insurance[];
}

export interface Group {
  id: string;
  projectId: string;
  name: LocalizedText;
  packagePrice: string;
  singleRoomSupplement?: string | null;
  invoiceDescription?: LocalizedText;
  depositDescription?: LocalizedText;
  invoiceRemark?: LocalizedText;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export function useProjects(includeInactive = false) {
  const queryParams = includeInactive ? '?includeInactive=true' : '';

  return useQuery({
    queryKey: ['projects', includeInactive],
    queryFn: () => api.get<Project[]>(`/projects${queryParams}`),
  });
}

export function useProject(id: string) {
  return useQuery({
    queryKey: ['projects', id],
    queryFn: () => api.get<Project>(`/projects/${id}`),
    enabled: !!id,
  });
}

export function useCreateProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ProjectFormData) => api.post<Project>('/projects', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      toast.success('Project aangemaakt');
    },
    onError: (error: Error) => {
      toast.error(`Fout bij aanmaken project: ${error.message}`);
    },
  });
}

export function useUpdateProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<ProjectFormData> }) =>
      api.put<Project>(`/projects/${id}`, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({ queryKey: ['projects', id] });
      toast.success('Project bijgewerkt');
    },
    onError: (error: Error) => {
      toast.error(`Fout bij bijwerken project: ${error.message}`);
    },
  });
}

export function useDeleteProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.delete(`/projects/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      toast.success('Project verwijderd');
    },
    onError: (error: Error) => {
      toast.error(`Fout bij verwijderen project: ${error.message}`);
    },
  });
}
